/**
 * PhotoCapture - Cross-platform photo capture component with three input modes:
 *
 *   1. **Camera** — Uses react-webcam for live camera preview and capture on both
 *      desktop and mobile. Requests camera permission via getUserMedia. Supports
 *      MacOS, Chrome, Edge, Safari, iOS, and Android.
 *   2. **File Upload** — Classic file picker dialog for selecting an existing image.
 *   3. **Drag and Drop** — Drop zone that accepts dragged image files.
 *
 * All captured/uploaded images go through the shared `compressPhoto` helper
 * (utils/imageCompression), which downscales and re-encodes to JPEG in a Web
 * Worker and guarantees the result is under the server's 10 MB upload cap.
 * Compression starts the instant a photo is taken or picked — the user never
 * asks for it — so the bytes are already small by the time anything tries to
 * send them over a field connection.
 *
 * The component renders as a dashed-border drop zone with action buttons inside.
 * When the camera is active, the drop zone is replaced by the live video feed
 * with a capture button overlay.
 *
 * @example
 * <PhotoCapture
 *     onCapture={handlePhotoCapture}
 *     label="Take Photo"
 * />
 */
import React, { useRef, useState, useCallback } from 'react';
import Webcam from 'react-webcam';
import { compressPhoto, dataUrlToFile, isHEICFile } from '../../../utils/imageCompression';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * Represents a captured and compressed photo ready for upload or offline storage.
 *
 * @property base64      - Compressed image as a base64 data URL (data:image/jpeg;base64,...)
 * @property file        - The compressed image as a File, ready to hand straight
 *                         to uploadService. Optional because a photo restored
 *                         from the offline queue is rehydrated from JSON, where
 *                         a File cannot survive — use `capturedPhotoToFile` to
 *                         get a File either way rather than reading this
 *                         directly.
 * @property fileName    - Generated or original filename
 * @property mimeType    - Always 'image/jpeg' after compression
 * @property sizeBytes   - Size in bytes of the compressed image data
 * @property capturedAt  - ISO 8601 timestamp of when the photo was captured
 */
export interface CapturedPhoto {
    base64: string;
    file?: File;
    fileName: string;
    mimeType: string;
    sizeBytes: number;
    capturedAt: string;
}

/**
 * Props for the PhotoCapture component.
 *
 * @property onCapture          - Callback fired with the compressed photo data after capture
 * @property maxSizeMB          - Target size in megabytes after compression. Omit for the
 *                                shared field-photo default (~1 MB) — the server's 10 MB
 *                                hard cap is always enforced regardless of what's passed.
 * @property maxWidthOrHeight   - Maximum pixel dimension for resizing. Omit for the shared
 *                                default (1600px).
 * @property label              - Button label text (default "Add Photo")
 * @property disabled           - When true, disables all capture controls
 */
export interface PhotoCaptureProps {
    onCapture: (photo: CapturedPhoto) => void;
    maxSizeMB?: number;
    maxWidthOrHeight?: number;
    label?: string;
    disabled?: boolean;
    /** Optional outer className merged onto the root element. */
    className?: string;
}

/* ============================================
 * HELPER FUNCTIONS
 * ============================================ */

/**
 * Returns the uploadable File for a captured photo.
 *
 * Prefers the File produced during compression — it's already in memory, so
 * this is free. Only falls back to decoding the base64 data URL for photos
 * that came back from the offline queue (JSON can't carry a File), which is
 * the one case worth paying the decode for.
 *
 * Every caller that uploads a CapturedPhoto should go through this rather
 * than hand-rolling `fetch(photo.base64)`; four copies of that had drifted
 * across the detail pages and the workflow organisms.
 */
export async function capturedPhotoToFile(photo: CapturedPhoto): Promise<File> {
    if (photo.file) return photo.file;
    return dataUrlToFile(photo.base64, photo.fileName, photo.mimeType);
}

/**
 * Runs the shared compression pipeline and packages the result as the
 * CapturedPhoto the rest of the app passes around. Both the File (for the
 * multipart upload) and the data URL (for previews and the offline queue)
 * come out of a single compression pass — encoding twice would double the
 * CPU cost on exactly the low-end phones we're trying to keep responsive.
 */
async function toCapturedPhoto(
    file: File,
    fileName: string,
    maxSizeMB?: number,
    maxWidthOrHeight?: number
): Promise<CapturedPhoto> {
    /* Log HEIC for awareness — browsers other than Safari can't decode it,
       and compressPhoto falls back to sending the original in that case. */
    if (isHEICFile(file)) {
        console.info('[PhotoCapture] HEIC file detected — will attempt client-side compression');
    }

    const compressed = await compressPhoto(file, { maxSizeMB, maxWidthOrHeight });

    return {
        base64: compressed.dataUrl,
        file: compressed.file,
        fileName: compressed.file.name || fileName,
        mimeType: compressed.file.type || 'image/jpeg',
        sizeBytes: compressed.sizeBytes,
        capturedAt: new Date().toISOString(),
    };
}

/* ============================================
 * COMPONENT
 * ============================================ */

const PhotoCapture: React.FC<PhotoCaptureProps> = ({
    onCapture,
    maxSizeMB,
    maxWidthOrHeight,
    label = 'Add Photo',
    disabled = false,
    className,
}) => {
    /* Ref to the hidden file input for the "Upload" button */
    const fileInputRef = useRef<HTMLInputElement>(null);

    /* Ref to react-webcam for programmatic screenshot capture */
    const webcamRef = useRef<Webcam>(null);

    /* Whether the live camera view is currently active */
    const [cameraActive, setCameraActive] = useState(false);

    /* Whether an image is currently being compressed */
    const [isCompressing, setIsCompressing] = useState(false);

    /* Error message to show the user */
    const [error, setError] = useState<string | null>(null);

    /* Whether a file is being dragged over the drop zone (visual highlight) */
    const [isDragOver, setIsDragOver] = useState(false);

    /* Which camera to use — 'user' (front) or 'environment' (rear).
       Default to environment (rear) which is more useful for field photos. */
    const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');

    /**
     * Processes a File through compression and emits the CapturedPhoto.
     * Shared by both file input and drag-and-drop handlers.
     */
    const processFile = useCallback(async (file: File) => {
        /* Only accept image files */
        if (!file.type.startsWith('image/') && !isHEICFile(file)) {
            setError('Please select an image file (JPEG, PNG, or HEIC).');
            return;
        }

        setIsCompressing(true);
        setError(null);

        try {
            onCapture(await toCapturedPhoto(file, file.name, maxSizeMB, maxWidthOrHeight));
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to process image';
            setError(message);
            console.error('[PhotoCapture] Compression error:', err);
        } finally {
            setIsCompressing(false);
        }
    }, [maxSizeMB, maxWidthOrHeight, onCapture]);

    /**
     * Handles file selection from the hidden <input type="file">.
     */
    const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        /* Reset the input value so the same file can be re-selected */
        event.target.value = '';
        await processFile(file);
    }, [processFile]);

    /**
     * Opens the file picker dialog by clicking the hidden input.
     */
    const handleUploadClick = useCallback(() => {
        setError(null);
        fileInputRef.current?.click();
    }, []);

    /* ---- Camera handlers ---- */

    /**
     * Opens the live camera view by requesting getUserMedia permission.
     */
    const handleOpenCamera = useCallback(() => {
        setError(null);
        setCameraActive(true);
    }, []);

    /**
     * Closes the live camera view and stops the video stream.
     */
    const handleCloseCamera = useCallback(() => {
        setCameraActive(false);
    }, []);

    /**
     * Toggles between front and rear camera.
     */
    const handleFlipCamera = useCallback(() => {
        setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'));
    }, []);

    /**
     * Captures a still frame from the webcam, compresses it, and emits.
     */
    const handleCameraCapture = useCallback(async () => {
        if (!webcamRef.current) return;

        setIsCompressing(true);
        setError(null);

        try {
            /* Grab a screenshot as a base64 JPEG data URL from react-webcam */
            const screenshot = webcamRef.current.getScreenshot();
            if (!screenshot) {
                setError('Failed to capture photo from camera.');
                setIsCompressing(false);
                return;
            }

            /* Convert to File for compression pipeline */
            const fileName = `camera-${Date.now()}.jpg`;
            const file = await dataUrlToFile(screenshot, fileName, 'image/jpeg');

            onCapture(await toCapturedPhoto(file, fileName, maxSizeMB, maxWidthOrHeight));
            /* Close camera after successful capture */
            setCameraActive(false);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to capture photo';
            setError(message);
            console.error('[PhotoCapture] Camera capture error:', err);
        } finally {
            setIsCompressing(false);
        }
    }, [maxSizeMB, maxWidthOrHeight, onCapture]);

    /* ---- Drag and drop handlers ---- */

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback(async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        /* Accept the first image file from the drop */
        const files = Array.from(e.dataTransfer.files);
        const imageFile = files.find((f) => f.type.startsWith('image/') || isHEICFile(f));

        if (!imageFile) {
            setError('Please drop an image file (JPEG, PNG, or HEIC).');
            return;
        }

        await processFile(imageFile);
    }, [processFile]);

    /**
     * Called when react-webcam encounters an error (e.g. permission denied).
     * Falls back gracefully — user can still use file upload or drag-and-drop.
     */
    const handleCameraError = useCallback((err: string | DOMException) => {
        console.warn('[PhotoCapture] Camera error:', err);
        setCameraActive(false);
        setError('Camera not available. Please use file upload or drag and drop instead.');
    }, []);

    /* ---- Render: Camera active view ---- */
    if (cameraActive) {
        return (
            <div className={['photo-capture', 'photo-capture--camera-active', 'flex flex-col gap-12px', className ?? ''].join(' ')} data-ref="organism-photo-capture">
                {/* Live camera preview with rounded corners */}
                <div className="photo-capture__viewfinder relative rounded-lg overflow-hidden border-2 border-brand-primary bg-black">
                    <Webcam
                        ref={webcamRef}
                        audio={false}
                        screenshotFormat="image/jpeg"
                        screenshotQuality={0.92}
                        videoConstraints={{
                            facingMode,
                            width: { ideal: 2000 },
                            height: { ideal: 2000 },
                        }}
                        onUserMediaError={handleCameraError}
                        className="w-full h-auto max-h-[60vh] object-contain"
                        /* Mirror front camera for natural selfie-style preview */
                        mirrored={facingMode === 'user'}
                    />

                    {/* Camera control overlay at the bottom of the video feed */}
                    <div className="photo-capture__camera-controls absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-16px flex items-center justify-center gap-16px">
                        {/* Flip camera button */}
                        <button type="button" onClick={handleFlipCamera} className="w-44px h-44px rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/40 transition-colors" aria-label="Flip camera">
                            <svg className="w-20px h-20px" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182" />
                            </svg>
                        </button>

                        {/* Capture (shutter) button — large circle */}
                        <button type="button" onClick={handleCameraCapture} disabled={isCompressing} className="w-64px h-64px rounded-full bg-white border-4 border-white/50 flex items-center justify-center hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg" aria-label="Capture photo">
                            {isCompressing ? (
                                <svg className="animate-spin h-24px w-24px text-brand-primary" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                            ) : (
                                <div className="w-48px h-48px rounded-full bg-brand-primary" />
                            )}
                        </button>

                        {/* Close camera button */}
                        <button type="button" onClick={handleCloseCamera} className="w-44px h-44px rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/40 transition-colors" aria-label="Close camera">
                            <svg className="w-20px h-20px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Error message below the camera view */}
                {error && (
                    <div className="photo-capture__error flex items-center gap-6px px-12px py-8px bg-red-50 border border-red-200 rounded-md">
                        <svg className="h-16px w-16px text-red-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                        </svg>
                        <p className="text-12px font-sans text-red-700">{error}</p>
                    </div>
                )}
            </div>
        );
    }

    /* ---- Render: Default view (drop zone + buttons) ---- */
    return (
        <div className={['photo-capture', 'flex flex-col gap-12px', className ?? ''].join(' ')} data-ref="organism-photo-capture">
            {/* Hidden file input for the Upload button */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/heic,image/heif,image/*"
                onChange={handleFileChange}
                className="hidden"
                aria-hidden="true"
                tabIndex={-1}
            />

            {/* Drop zone area — accepts dragged image files.
                Highlighted with brand color border when dragging over. */}
            <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={[
                    'flex flex-col items-center justify-center gap-12px p-20px rounded-lg border-2 border-dashed transition-colors min-h-[160px]',
                    isDragOver
                        ? 'border-brand-primary bg-brand-primary/5'
                        : 'border-gray-300 bg-gray-50',
                    disabled ? 'opacity-50 cursor-not-allowed' : '',
                ].join(' ')}
            >
                {/* Compressing state overlay */}
                {isCompressing ? (
                    <div className="flex flex-col items-center gap-8px">
                        <svg className="animate-spin h-28px w-28px text-brand-primary" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        <span className="text-14px font-sans font-medium text-brand-text-muted">Optimizing photo…</span>
                    </div>
                ) : (
                    <>
                        {/* Cloud upload icon */}
                        <svg className="h-32px w-32px text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
                        </svg>

                        {/* Drag-and-drop hint text */}
                        <p className="text-14px font-sans text-gray-500 text-center">
                            Drag and drop an image here, or use the buttons below
                        </p>

                        {/* Action buttons row: Camera + Upload */}
                        <div className="flex items-center gap-8px mt-4px">
                            {/* Camera button — opens live webcam view */}
                            <button
                                type="button"
                                onClick={handleOpenCamera}
                                disabled={disabled}
                                className="inline-flex items-center gap-6px px-16px py-10px text-14px font-sans font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed rounded-lg min-h-44px transition-colors shadow-sm"
                                aria-label="Open camera"
                            >
                                {/* Camera icon */}
                                <svg className="h-18px w-18px" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Z" />
                                </svg>
                                Camera
                            </button>

                            {/* Upload button — opens native file picker */}
                            <button
                                type="button"
                                onClick={handleUploadClick}
                                disabled={disabled}
                                className="inline-flex items-center gap-6px px-16px py-10px text-14px font-sans font-semibold text-brand-primary bg-white border border-brand-primary hover:bg-brand-primary/10 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg min-h-44px transition-colors"
                                aria-label="Upload photo"
                            >
                                {/* Upload icon */}
                                <svg className="h-18px w-18px" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                                </svg>
                                Upload
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* Error message below the drop zone */}
            {error && (
                <div className="photo-capture__error flex items-center gap-6px px-12px py-8px bg-red-50 border border-red-200 rounded-md">
                    <svg className="h-16px w-16px text-red-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                    </svg>
                    <p className="text-12px font-sans text-red-700">{error}</p>
                </div>
            )}
        </div>
    );
};

export default PhotoCapture;
